import { useState } from 'react';
import { X, Image as ImageIcon, MapPin, Loader2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { compressImages } from '@/lib/imageCompression';

interface RunSegment {
  id: string;
  trail_segment?: {
    trail_name?: string;
  };
}

interface RunPhotoManagerProps {
  photos: string[];
  runId: string;
  segments: RunSegment[];
  onPhotosUpdate: (photos: string[]) => void;
}

const RunPhotoManager = ({ photos, runId, segments, onPhotosUpdate }: RunPhotoManagerProps) => {
  const { toast } = useToast();
  const [photoSegments, setPhotoSegments] = useState<{ [photoUrl: string]: string }>({});
  const [uploading, setUploading] = useState(false);
  const [compressing, setCompressing] = useState(false);

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setCompressing(true);
    const uploadedUrls: string[] = [];

    try {
      // Compress images first
      const compressedFiles = await compressImages(Array.from(files), {
        maxWidth: 2048,
        maxHeight: 2048,
        quality: 0.8,
        preserveExif: true,
      });
      
      setCompressing(false);
      setUploading(true);

      for (const file of compressedFiles) {
        const fileExt = file.name.split('.').pop();
        const fileName = `${runId}/${Math.random()}.${fileExt}`;

        const { error: uploadError, data } = await supabase.storage
          .from('run-photos')
          .upload(fileName, file);

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from('run-photos')
          .getPublicUrl(fileName);

        uploadedUrls.push(publicUrl);
      }

      const updatedPhotos = [...photos, ...uploadedUrls];
      await supabase
        .from('runs')
        .update({ photo_urls: updatedPhotos })
        .eq('id', runId);

      onPhotosUpdate(updatedPhotos);
      toast({
        title: "Photos uploaded",
        description: `${uploadedUrls.length} photo(s) compressed and added successfully.`,
      });
    } catch (error) {
      console.error('Error uploading photos:', error);
      toast({
        title: "Upload failed",
        description: "Failed to upload photos. Please try again.",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
      setCompressing(false);
    }
  };

  const handleRemovePhoto = async (photoUrl: string) => {
    const updatedPhotos = photos.filter(p => p !== photoUrl);
    
    try {
      await supabase
        .from('runs')
        .update({ photo_urls: updatedPhotos })
        .eq('id', runId);

      onPhotosUpdate(updatedPhotos);
      toast({
        title: "Photo removed",
        description: "Photo removed successfully.",
      });
    } catch (error) {
      console.error('Error removing photo:', error);
      toast({
        title: "Error",
        description: "Failed to remove photo.",
        variant: "destructive",
      });
    }
  };

  const handleSegmentAssignment = (photoUrl: string, segmentId: string) => {
    setPhotoSegments(prev => ({
      ...prev,
      [photoUrl]: segmentId
    }));
    // In a full implementation, this would save to a photo_segments table
    toast({
      title: "Segment assigned",
      description: "Photo assigned to trail segment.",
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ImageIcon className="h-5 w-5" />
          Photo Gallery Management
        </CardTitle>
        <CardDescription>
          Upload new photos and assign them to trail segments
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Upload button */}
        <div>
          <input
            type="file"
            id="photo-upload"
            multiple
            accept="image/*"
            onChange={handlePhotoUpload}
            className="hidden"
          />
          <Button
            variant="outline"
            onClick={() => document.getElementById('photo-upload')?.click()}
            disabled={uploading || compressing}
          >
            {compressing ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <ImageIcon className="h-4 w-4 mr-2" />
            )}
            {compressing ? 'Compressing...' : uploading ? 'Uploading...' : 'Upload Photos'}
          </Button>
        </div>

        {/* Photo grid */}
        {photos.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {photos.map((photo, index) => (
              <div key={index} className="space-y-2">
                <div className="relative aspect-square rounded-lg overflow-hidden bg-muted group">
                  <img
                    src={photo}
                    alt={`Photo ${index + 1}`}
                    className="w-full h-full object-cover photo-signature"
                  />
                  <Button
                    variant="destructive"
                    size="icon"
                    className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={() => handleRemovePhoto(photo)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                
                {/* Segment assignment */}
                {segments.length > 0 && (
                  <Select
                    value={photoSegments[photo] || ''}
                    onValueChange={(value) => handleSegmentAssignment(photo, value)}
                  >
                    <SelectTrigger className="text-xs">
                      <SelectValue placeholder="Assign to segment" />
                    </SelectTrigger>
                    <SelectContent>
                      {segments.map((segment) => (
                        <SelectItem key={segment.id} value={segment.id}>
                          {segment.trail_segment?.trail_name || 'Unknown Trail'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {photoSegments[photo] && (
                  <Badge variant="secondary" className="text-xs gap-1">
                    <MapPin className="h-3 w-3" />
                    Assigned
                  </Badge>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            No photos uploaded yet. Click the button above to add photos.
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default RunPhotoManager;
